/**
 * Examples Page
 * Realistic waste management plan examples across different project types
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Material {
  name: string;
  percentage: number;
  tons: number;
}

interface ExamplePlan {
  id: string;
  title: string;
  category: 'commercial' | 'residential' | 'demolition' | 'renovation' | 'mixed-use';
  projectDetails: {
    type: string;
    size: string;
    location: string;
    estimatedWaste: string;
  };
  estimation: {
    method: string;
    calculation: string;
    notes: string[];
  };
  materials: Material[];
  strategy: {
    approach: string[];
    facilities: string[];
  };
  diversionRate: number;
  lessonsLearned: string[];
}

const examples: ExamplePlan[] = [
  {
    id: 'commercial-tenant-improvement',
    title: 'Small Commercial Tenant Improvement',
    category: 'commercial',
    projectDetails: {
      type: '2,500 sq ft office remodel',
      size: '2,500 square feet',
      location: 'San Francisco, CA',
      estimatedWaste: '15 tons',
    },
    estimation: {
      method: 'Square footage × renovation factor',
      calculation: '2,500 sq ft × 12 lbs/sq ft ÷ 2,000 = 15 tons',
      notes: [
        'Used 12 lbs/sq ft for interior renovation (higher than new construction due to demolition)',
        'Included removal of existing drywall, ceiling grid, and flooring',
        'Packaging from new materials adds ~10% to total',
      ],
    },
    materials: [
      { name: 'Drywall', percentage: 40, tons: 6.0 },
      { name: 'Metal Studs & Grid', percentage: 20, tons: 3.0 },
      { name: 'Wood Framing', percentage: 15, tons: 2.25 },
      { name: 'Cardboard Packaging', percentage: 10, tons: 1.5 },
      { name: 'Mixed C&D', percentage: 15, tons: 2.25 },
    ],
    strategy: {
      approach: [
        'Dedicated bins for drywall, metal, wood, and cardboard',
        'Source separation by trade contractors',
        'Reused some existing ceiling grid and door frames',
        'Mixed C&D sent to sorting facility',
      ],
      facilities: [
        'Recology San Francisco - Mixed C&D Processing (3.5 miles)',
        'Bayshore Recycling - Metal Recycling (6 miles)',
        'Sunset Scavenger - Cardboard Recycling (4 miles)',
      ],
    },
    diversionRate: 72,
    lessonsLearned: [
      'Keeping drywall separate was crucial - contaminated drywall cannot be recycled',
      'Cardboard boxes arrived flat, making storage easier before recycling',
      'Coordinate bin delivery timing with demolition phase to avoid site clutter',
      'Small project meant frequent trips to recycling facilities',
    ],
  },
  {
    id: 'large-new-construction',
    title: 'Large New Construction',
    category: 'commercial',
    projectDetails: {
      type: '50,000 sq ft medical office building',
      size: '50,000 square feet',
      location: 'Los Angeles, CA',
      estimatedWaste: '450 tons',
    },
    estimation: {
      method: 'Square footage × construction factor with multiplier',
      calculation: '50,000 sq ft × 4.5 lbs/sq ft × 2 ÷ 2,000 = 225 tons (construction)\n+ 225 tons (estimated sitework & concrete waste) = 450 tons total',
      notes: [
        'Used 4.5 lbs/sq ft for commercial construction (medical has more systems)',
        '2× multiplier accounts for excavation, formwork, and packaging',
        'Concrete waste from foundation work adds significantly',
      ],
    },
    materials: [
      { name: 'Concrete & Masonry', percentage: 35, tons: 157.5 },
      { name: 'Wood & Lumber', percentage: 25, tons: 112.5 },
      { name: 'Drywall', percentage: 15, tons: 67.5 },
      { name: 'Cardboard Packaging', percentage: 10, tons: 45.0 },
      { name: 'Metal', percentage: 8, tons: 36.0 },
      { name: 'Mixed C&D', percentage: 7, tons: 31.5 },
    ],
    strategy: {
      approach: [
        'Dedicated on-site bins for each major material stream',
        'Weekly coordination meetings with all trade contractors',
        'Designated waste coordinator throughout project duration',
        'Pre-purchased materials to reduce packaging waste',
        'Salvaged formwork for reuse on future projects',
      ],
      facilities: [
        'CR&R Environmental Services - Mixed C&D (8 miles)',
        'Recon Recycling - Concrete Crushing (12 miles)',
        'SA Recycling - Metal Recycling (7 miles)',
        'California Waste Services - Cardboard & Wood (9 miles)',
      ],
    },
    diversionRate: 78,
    lessonsLearned: [
      'Having a dedicated waste coordinator improved diversion by 15% vs. previous projects',
      'Scheduling bin swaps during off-hours reduced site disruption',
      'Wood contamination from concrete slurry was biggest challenge',
      'Medical equipment packaging required special handling',
      'Early planning with subs about waste separation crucial to success',
    ],
  },
  {
    id: 'residential-demolition',
    title: 'Residential Demolition',
    category: 'demolition',
    projectDetails: {
      type: 'Single-family home demolition',
      size: '1,800 sq ft home + detached garage',
      location: 'San Diego, CA',
      estimatedWaste: '120 tons',
    },
    estimation: {
      method: 'Building size × demolition factor',
      calculation: '1,800 sq ft × ~130 lbs/sq ft ÷ 2,000 = 117 tons\n+ 3 tons (garage & misc.) = 120 tons',
      notes: [
        'Full demolition generates 100-150 lbs/sq ft depending on construction type',
        '1950s wood-frame house with concrete foundation and driveway',
        'Included removal of landscaping hardscape',
      ],
    },
    materials: [
      { name: 'Concrete & Foundation', percentage: 50, tons: 60.0 },
      { name: 'Wood Framing & Debris', percentage: 30, tons: 36.0 },
      { name: 'Asphalt Roofing', percentage: 10, tons: 12.0 },
      { name: 'Metal (windows, pipes)', percentage: 5, tons: 6.0 },
      { name: 'Mixed Debris', percentage: 5, tons: 6.0 },
    ],
    strategy: {
      approach: [
        'Soft demolition first - salvaged cabinets, fixtures, and doors',
        'Separated metal (copper pipes, aluminum windows) before structural demo',
        'On-site concrete crushing reduced hauling costs',
        'Used crushed concrete as base material for new construction',
        'Systematic deconstruction vs. wholesale demo',
      ],
      facilities: [
        'On-site concrete crushing (mobile crusher)',
        'Habitat for Humanity ReStore - Salvaged materials donation',
        'Miramar Greenery - Wood chipping (22 miles)',
        'Sims Metal Management - Metal recycling (15 miles)',
      ],
    },
    diversionRate: 82,
    lessonsLearned: [
      'Soft demolition added 3 days but saved significant disposal costs',
      'On-site crushing was cost-effective for this volume of concrete',
      'Pre-1978 paint required lead testing before recycling wood',
      'Salvaged materials donation provided tax deduction benefit',
      'Asbestos and hazmat testing essential before starting - delayed project 1 week',
    ],
  },
  {
    id: 'mid-size-renovation',
    title: 'Mid-Size Retail Renovation',
    category: 'renovation',
    projectDetails: {
      type: '8,000 sq ft retail space conversion',
      size: '8,000 square feet',
      location: 'Sacramento, CA',
      estimatedWaste: '35 tons',
    },
    estimation: {
      method: 'Square footage × renovation factor',
      calculation: '8,000 sq ft × 8.75 lbs/sq ft ÷ 2,000 = 35 tons',
      notes: [
        'Converting clothing store to restaurant - significant MEP and finishes work',
        'Factor includes demolition of existing plus new construction waste',
        'Kitchen equipment packaging substantial',
      ],
    },
    materials: [
      { name: 'Drywall', percentage: 35, tons: 12.25 },
      { name: 'Wood & Lumber', percentage: 20, tons: 7.0 },
      { name: 'Fixtures & Displays', percentage: 15, tons: 5.25 },
      { name: 'Cardboard', percentage: 12, tons: 4.2 },
      { name: 'Metal', percentage: 10, tons: 3.5 },
      { name: 'Mixed C&D', percentage: 8, tons: 2.8 },
    ],
    strategy: {
      approach: [
        'Donated existing retail fixtures to local nonprofit',
        'Coordinated with equipment suppliers to take back packaging',
        'Source-separated drywall during demolition phase',
        'Reused existing ceiling grid where possible',
        'Material-specific recycling for all major streams',
      ],
      facilities: [
        'Sacramento Recycling & Transfer Station - Mixed C&D (4 miles)',
        'Habitat for Humanity ReStore - Fixture donations (local)',
        'Western Metals - Metal recycling (6 miles)',
        'Waste Management - Cardboard recycling (5 miles)',
      ],
    },
    diversionRate: 68,
    lessonsLearned: [
      'Fixture donation required 2-week lead time for pickup scheduling',
      'Equipment suppliers willing to take back packaging if arranged in advance',
      'Restaurant hood installation generated unexpected amount of metal scrap',
      'Tight urban location made bin placement challenging',
      'Daily cleanup crucial in active retail center',
    ],
  },
  {
    id: 'mixed-use-development',
    title: 'Mixed-Use Development',
    category: 'mixed-use',
    projectDetails: {
      type: '15 residential units + ground-floor retail',
      size: '25,000 sq ft total (18,000 residential + 7,000 retail)',
      location: 'Oakland, CA',
      estimatedWaste: '280 tons',
    },
    estimation: {
      method: 'Square footage × construction factors by use',
      calculation: 'Residential: 18,000 × 6 lbs/sq ft = 108,000 lbs\nRetail: 7,000 × 4.5 lbs/sq ft = 31,500 lbs\nTotal: 139,500 lbs × 2 (construction + sitework) ÷ 2,000 = 280 tons',
      notes: [
        'Residential generates more waste per sq ft than commercial',
        'Multi-unit construction includes significant repetitive framing',
        'Ground-floor retail required heavier structural systems',
      ],
    },
    materials: [
      { name: 'Concrete', percentage: 30, tons: 84.0 },
      { name: 'Wood Framing', percentage: 28, tons: 78.4 },
      { name: 'Drywall', percentage: 18, tons: 50.4 },
      { name: 'Cardboard Packaging', percentage: 10, tons: 28.0 },
      { name: 'Metal', percentage: 8, tons: 22.4 },
      { name: 'Mixed C&D', percentage: 6, tons: 16.8 },
    ],
    strategy: {
      approach: [
        'Full-time waste coordinator for 14-month project',
        'Phased approach: foundation, framing, MEP, finishes',
        'Separate bins for each major material, swapped weekly',
        'Weekly tailgate meetings with crews on waste separation',
        'Incentive program for trades meeting diversion goals',
        'Monthly diversion tracking and reporting',
      ],
      facilities: [
        'Davis Street Transfer Station - Mixed C&D (5 miles)',
        'Alameda County Industries - Concrete recycling (8 miles)',
        'Green Waste Recovery - Wood recycling (7 miles)',
        'Strategic Materials - Metal recycling (6 miles)',
        'Republic Services - Cardboard recycling (4 miles)',
      ],
    },
    diversionRate: 75,
    lessonsLearned: [
      'Waste coordinator ROI was clear - prevented contamination issues',
      'Crew incentive program improved participation significantly',
      'Phased approach allowed focusing on specific materials per phase',
      'Monthly tracking helped identify problem areas early',
      'Unit repetition meant predictable waste generation patterns',
      'Coordination with multiple trades required clear communication protocol',
    ],
  },
];

interface ProgressBarProps {
  percentage: number;
  label: string;
}

function ProgressBar({ percentage, label }: ProgressBarProps) {
  const getColor = (pct: number) => {
    if (pct >= 75) return 'bg-green-500';
    if (pct >= 65) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-neutral-700">{label}</span>
        <span className="font-semibold text-neutral-900">{percentage}%</span>
      </div>
      <div className="w-full bg-neutral-200 rounded-full h-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getColor(percentage)}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

interface MaterialBarProps {
  material: Material;
}

function MaterialBar({ material }: MaterialBarProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-neutral-700">{material.name}</span>
        <span className="text-neutral-600">
          {material.tons} tons ({material.percentage}%)
        </span>
      </div>
      <div className="w-full bg-neutral-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-full rounded-full bg-primary-500"
          style={{ width: `${material.percentage}%` }}
        />
      </div>
    </div>
  );
}

interface ExampleCardProps {
  example: ExamplePlan;
  isOpen: boolean;
  onClick: () => void;
}

function ExampleCard({ example, isOpen, onClick }: ExampleCardProps) {
  const totalTons = parseFloat(example.projectDetails.estimatedWaste.replace(' tons', ''));
  const divertedTons = (totalTons * example.diversionRate) / 100;

  return (
    <div className="border-2 border-neutral-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={onClick}
        className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors text-left"
      >
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-neutral-900 mb-1">{example.title}</h3>
          <p className="text-sm text-neutral-600">
            {example.projectDetails.type} • {example.projectDetails.location}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-600">{example.diversionRate}%</div>
            <div className="text-xs text-neutral-500">diverted</div>
          </div>
          <svg
            className={`w-6 h-6 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="px-6 py-6 bg-neutral-50 border-t-2 border-neutral-200 space-y-6">
          {/* Project Details */}
          <section>
            <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-primary-600">📋</span>
              Project Details
            </h4>
            <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">Project Type:</span>
                <span className="font-medium text-neutral-900">{example.projectDetails.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Size:</span>
                <span className="font-medium text-neutral-900">{example.projectDetails.size}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Location:</span>
                <span className="font-medium text-neutral-900">{example.projectDetails.location}</span>
              </div>
              <div className="flex justify-between border-t border-neutral-200 pt-2 mt-2">
                <span className="text-neutral-600">Total Waste:</span>
                <span className="font-semibold text-neutral-900">{example.projectDetails.estimatedWaste}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Diverted:</span>
                <span className="font-semibold text-green-600">{divertedTons.toFixed(1)} tons</span>
              </div>
            </div>
          </section>

          {/* Waste Estimation */}
          <section>
            <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-primary-600">🧮</span>
              Waste Estimation Methodology
            </h4>
            <div className="bg-white rounded-lg p-4 space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Method</div>
                <div className="text-sm text-neutral-800">{example.estimation.method}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Calculation</div>
                <div className="text-sm text-neutral-800 font-mono whitespace-pre-line bg-neutral-50 p-2 rounded">
                  {example.estimation.calculation}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Notes</div>
                <ul className="text-sm text-neutral-700 space-y-1">
                  {example.estimation.notes.map((note, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-neutral-400 mt-1">•</span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Material Breakdown */}
          <section>
            <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-primary-600">📊</span>
              Material Breakdown
            </h4>
            <div className="bg-white rounded-lg p-4 space-y-3">
              {example.materials.map((material, idx) => (
                <MaterialBar key={idx} material={material} />
              ))}
            </div>
          </section>

          {/* Diversion Strategy */}
          <section>
            <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-primary-600">♻️</span>
              Diversion Strategy
            </h4>
            <div className="bg-white rounded-lg p-4 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Approach</div>
                <ul className="text-sm text-neutral-700 space-y-2">
                  {example.strategy.approach.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-green-600 font-bold mt-0.5">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Facilities Used</div>
                <ul className="text-sm text-neutral-700 space-y-1">
                  {example.strategy.facilities.map((facility, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">→</span>
                      <span>{facility}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Diversion Rate */}
          <section>
            <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-primary-600">🎯</span>
              Final Diversion Rate
            </h4>
            <div className="bg-white rounded-lg p-4">
              <ProgressBar percentage={example.diversionRate} label="Waste Diverted from Landfill" />
              <div className="mt-3 text-sm text-neutral-600">
                {example.diversionRate >= 75 ? (
                  <span className="text-green-700">
                    <strong>Excellent!</strong> Well above the 65% CalGreen requirement.
                  </span>
                ) : example.diversionRate >= 65 ? (
                  <span className="text-yellow-700">
                    <strong>Compliant.</strong> Meets the 65% CalGreen requirement.
                  </span>
                ) : (
                  <span className="text-red-700">
                    <strong>Non-compliant.</strong> Below the 65% CalGreen requirement.
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Lessons Learned */}
          <section>
            <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
              <span className="text-primary-600">💡</span>
              Lessons Learned
            </h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ul className="text-sm text-blue-900 space-y-2">
                {example.lessonsLearned.map((lesson, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-blue-600 mt-1">▸</span>
                    <span>{lesson}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export function Examples() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'All Examples' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'residential', label: 'Residential' },
    { value: 'demolition', label: 'Demolition' },
    { value: 'renovation', label: 'Renovation' },
    { value: 'mixed-use', label: 'Mixed-Use' },
  ];

  const filteredExamples =
    selectedCategory === 'all'
      ? examples
      : examples.filter((ex) => ex.category === selectedCategory);

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Example Plans</h1>

      <p className="text-lg text-neutral-600 mb-8">
        Realistic waste management plans showing what actually works across different project types
      </p>

      {/* Category Filter */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => {
                setSelectedCategory(category.value);
                setOpenIndex(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                selectedCategory === category.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Examples List */}
      <div className="space-y-4 mb-12">
        {filteredExamples.map((example, index) => (
          <ExampleCard
            key={example.id}
            example={example}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>

      {/* Key Takeaways */}
      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-12">
        <h2 className="text-2xl font-semibold text-green-900 mb-4 flex items-center gap-2">
          <span>🔑</span>
          Key Takeaways & Best Practices
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-green-900 mb-3">Planning & Estimation</h3>
            <ul className="text-sm text-green-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Use project-specific factors: 6 lbs/sq ft residential, 3.9-4.5 lbs/sq ft commercial</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Demolition and renovation generate 2-3× more waste than new construction</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Include packaging waste (typically 10-15% of total)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Account for sitework, formwork, and excavation materials</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-green-900 mb-3">Execution Strategy</h3>
            <ul className="text-sm text-green-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Source separation is critical - contaminated materials cannot be recycled</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Designate a waste coordinator for projects over $500k</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Hold regular crew meetings about waste separation procedures</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Establish relationships with facilities before project starts</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-green-900 mb-3">Material-Specific Tips</h3>
            <ul className="text-sm text-green-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Concrete, metal, and clean wood are easiest and most valuable to recycle</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Drywall must be kept clean and dry for recycling</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Salvage and donate fixtures before demolition phase</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span>Coordinate with suppliers on packaging take-back programs</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-green-900 mb-3">Common Pitfalls</h3>
            <ul className="text-sm text-green-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✗</span>
                <span>Waiting until project starts to find recycling facilities</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✗</span>
                <span>Not training crews on separation procedures</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✗</span>
                <span>Allowing material contamination (concrete in wood bins, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✗</span>
                <span>Forgetting to get weight tickets from all facilities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Learn More</h3>
        <div className="space-y-2">
          <Link
            to="/docs/calgreen-overview"
            className="block text-primary-600 hover:text-primary-700 font-medium"
          >
            → CalGreen 5.408 Overview
          </Link>
          <Link to="/docs/faq" className="block text-primary-600 hover:text-primary-700 font-medium">
            → Frequently Asked Questions
          </Link>
          <Link to="/" className="block text-primary-600 hover:text-primary-700 font-medium">
            → Create Your Plan
          </Link>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          <strong>Note:</strong> These examples are based on typical project experiences and industry standards.
          Actual waste generation and diversion rates will vary based on project specifics, contractor practices,
          available facilities, and local conditions. Use these examples as guidelines for planning, but always
          verify with your specific project team and local recycling facilities.
        </p>
      </div>
    </div>
  );
}